import { ExtractPayloadType } from "@hyper-fetch/core";
import { useSubmit } from "@hyper-fetch/react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { client } from "@/api/client";
import { sdk } from "@/api/sdk";

export type OAuthIntent = "signup" | "login";

export const useAuth = () => {
  const navigate = useNavigate();
  const loginResponse = useSubmit(sdk.auth.login.$post);
  const signupResponse = useSubmit(sdk.auth.signup.$post);
  const googleResponse = useSubmit(sdk.auth.google.$intent.$get);

  const signIn = useCallback(
    async (payload: ExtractPayloadType<typeof sdk.auth.login.$post>) => {
      return loginResponse.submit({ payload });
    },
    [loginResponse],
  );

  const signUp = useCallback(
    async (payload: ExtractPayloadType<typeof sdk.auth.signup.$post>) => {
      return signupResponse.submit({ payload });
    },
    [signupResponse],
  );

  const signInWithGoogle = useCallback(
    async (intent: OAuthIntent = "login") => {
      const { data } = await googleResponse.submit({ params: { intent }, queryParams: {} });
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    },
    [googleResponse],
  );

  const signOut = useCallback(async () => {
    await sdk.auth.logout.$get.send();
    const meCacheKey = sdk.users.me.$get.cacheKey;
    client.cache.delete(meCacheKey);
    client.cache.invalidate(/\/users\/me/);
    await client.cache.clear();
    navigate({ to: "/login" });
  }, [navigate]);

  return {
    signIn,
    signUp,
    signOut,
    loginResponse,
    signupResponse,
    signInWithGoogle,
    googleSubmitting: googleResponse.submitting,
  };
};
